import React, { HTMLAttributes, Ref } from 'react';
import { useCombobox, UseComboboxStateChange } from 'downshift';

import ConfidenceLabel, { ConfidenceLabelProps } from '../ConfidenceLabel';
import styles from './Combobox.module.css';

export type ComboboxProps<T> = ConfidenceLabelProps &
  HTMLAttributes<HTMLDivElement> & {
    onSelectedItemChange?: (changes: UseComboboxStateChange<T>) => void;
    onInputValueChange?: (changes: UseComboboxStateChange<T>) => void;
    selectedItem?: T;
    items: Array<T>;
    itemDisplayFormat?: (item: T | null) => string;
    innerRef?: Ref<HTMLInputElement>;
    innerTabIndex?: number;
    ref?: Ref<HTMLDivElement>;
    disabled?: boolean;
    error?: boolean;
    warn?: boolean;
    name?: string;
    value?: string;
  };

const defaultItemDisplayFormat = <T,>(item: T | null) => {
  if (!item) {
    return '';
  }

  if (typeof item === 'string') {
    return item;
  }

  if (typeof item === 'object') {
    const coercedItem = (item as unknown) as any;
    if (coercedItem.hasOwnProperty('name')) {
      return coercedItem.name!; // eslint-disable-line
    }
    if (coercedItem.hasOwnProperty('display')) {
      return coercedItem.display!; // eslint-disable-line
    }
    return JSON.stringify(coercedItem);
  }
  return '';
};

export const Combobox = <T,>({
  selectedItem,
  disabled,
  error,
  warn,
  onSelectedItemChange,
  onInputValueChange,
  items,
  itemDisplayFormat = defaultItemDisplayFormat,
  confidenceAutomated,
  confidenceLevel,
  confidenceValue,
  className,
  innerRef,
  ref,
  id,
  placeholder,
  innerTabIndex,
  name: inputName,
  value,
  ...divProps
}: ComboboxProps<T>): JSX.Element => {
  const {
    openMenu,
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox<T>({
    itemToString: itemDisplayFormat,
    items: items,
    selectedItem: selectedItem || undefined,
    onSelectedItemChange: onSelectedItemChange,
    onInputValueChange: onInputValueChange,
    inputValue: value,
  });

  const automatedClassName = confidenceAutomated && !isOpen ? ' automated' : '';

  const disabledClassName = disabled ? styles['input-append-disabled'] : '';
  const extraClassNames = className ? ` ${className}` : '';
  const errorClassName = error ? ` ${styles.error}` : '';
  const warnClassName = warn ? ` ${styles.warn}` : '';
  const classNames = `form-control form-control-appended${automatedClassName}${extraClassNames}${warnClassName}${errorClassName} ${styles.input}`;

  const containerClasses = `${styles.container} ${className ? className : ''}`;

  const focusInput = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    // eslint-disable-next-line
    // @ts-expect-error
    event.target.previousSibling?.focus();
  };

  const innerInputProps = getInputProps({
    ref: innerRef,
    id,
    placeholder,
    disabled,
    name: inputName,
    // onClick: openMenu,
  });

  return (
    <div {...divProps} className={containerClasses} ref={ref}>
      <div {...getComboboxProps()}>
        <div className="input-group input-group-merge">
          <input {...innerInputProps} className={classNames} disabled={disabled} tabIndex={innerTabIndex || 0} />
          {confidenceAutomated ? (
            <span className={`${styles['badge-automated']} ${styles['badge-label']}`} onClick={focusInput}>
              Automated
            </span>
          ) : null}
          <div className={`input-group-append${warnClassName}${errorClassName}`}>
            <div
              className={`input-group-text${automatedClassName}${warnClassName}${errorClassName} ${disabledClassName}`}
            >
              {!isOpen && (
                <ConfidenceLabel
                  confidenceAutomated={confidenceAutomated}
                  confidenceLevel={confidenceLevel}
                  confidenceValue={confidenceValue}
                />
              )}
              <button type="button" {...getToggleButtonProps()} aria-label="toggle menu" className={styles.button}>
                <span className={`fe fe-chevron-${isOpen ? 'up' : 'down'}`}></span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <ul {...getMenuProps()} className={`${styles.menu} ${isOpen ? 'open' : ''}`}>
        {isOpen &&
          items.map((item, index) => (
            <li
              key={`${itemDisplayFormat(item)}${index}`}
              className={highlightedIndex === index ? styles['highlighted'] : undefined}
              {...getItemProps({ item, index })}
            >
              {itemDisplayFormat(item)}
            </li>
          ))}
      </ul>
    </div>
  );
};
